import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { ArrowLeft, ArrowRight, Rocket } from 'lucide-react';
import { getGetProfileQueryKey } from '@biasafe.queries/user/user';
import { useMarkWelcomeTourSeen } from '@biasafe.queries/user-welcome-tour-seen/user-welcome-tour-seen';
import { TOUR_PAGES } from './tourPages';
import type { UserProfile } from '@biasafe.models';
import type { ReactNode } from 'react';
import type { TourArt } from './tourPages';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { cn } from '@/lib/utils';

/**
 * The first-visit welcome tour, for org admins.
 *
 * WHO SEES IT is the server's call, through `showWelcomeTour` on the profile:
 * an admin whose account has no `welcome_tour_seen_at`. It is on the account
 * and not in localStorage so it is shown once per PERSON — a second computer
 * does not greet them again, and a colleague on a shared machine is not told
 * they have already seen it. The `orgRole` check below repeats the server's
 * rule rather than replacing it; it only stops a stale cached profile from
 * opening the tour for someone who has since been made a member.
 *
 * EVERY WAY OUT COUNTS AS SEEN: Skip, the last page's button, the ×, Escape, a
 * click outside, and the Settings button on the firm-forms page. All of them
 * are the reader saying "enough", and a tour that came back because they left
 * by the wrong door would be the one thing about it they remember.
 *
 * It closes at once and records the dismissal in the background. Waiting on
 * the POST would hold a modal open on a slow network after the reader has
 * asked it to go. If the POST fails, the tour comes back on their next visit —
 * the cheaper of the two wrongs, since the alternative is it never showing.
 */
export function WelcomeTour({ profile }: { profile: UserProfile | undefined }) {
  const eligible =
    profile?.orgRole === 'admin' && profile.showWelcomeTour === true;

  // Closing is remembered locally as well, because the profile in the cache
  // still says "show" until the POST comes back, and without this the dialog
  // would reopen in the gap.
  const [dismissed, setDismissed] = useState(false);
  const [step, setStep] = useState(0);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reducedMotion = useReducedMotion();
  const { mutate: markSeen } = useMarkWelcomeTourSeen({
    mutation: {
      // The response IS the profile, flag already cleared, so it goes straight
      // into the cache rather than costing a refetch.
      onSuccess: (fresh) => {
        queryClient.setQueryData(getGetProfileQueryKey(), fresh);
      },
    },
  });

  if (!eligible || dismissed) return null;

  const page = TOUR_PAGES[step];
  const isFirst = step === 0;
  const isLast = step === TOUR_PAGES.length - 1;

  const close = () => {
    setDismissed(true);
    markSeen();
  };

  const goTo = (href: string) => {
    close();
    void navigate({ href });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto p-0 sm:max-w-xl"
        data-testid="welcome-tour"
      >
        <TourIllustration art={page.art} step={step} still={reducedMotion} />

        <motion.div
          // Keyed on the step so each page rises in fresh. Enter only, with no
          // exit: waiting for the old page to leave would make Next feel slow,
          // and there is nothing on the outgoing page worth watching go.
          key={page.id}
          initial={reducedMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="px-6 pt-5 pb-2 sm:px-8"
        >
          <p className="text-xs font-medium tracking-wide text-primary uppercase">
            {page.eyebrow}
          </p>
          <DialogTitle className="mt-1.5 text-xl leading-tight text-foreground">
            {page.title}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed">
            {page.body}
          </DialogDescription>

          <ul className="mt-4 space-y-2.5">
            {page.points.map((point) => {
              const Icon = point.icon;
              return (
                <li
                  key={point.text}
                  className="flex items-start gap-3 text-sm text-foreground"
                >
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="size-3.5" aria-hidden />
                  </span>
                  <span className="leading-relaxed">{point.text}</span>
                </li>
              );
            })}
          </ul>

          {page.action && (
            <Button
              variant="outline"
              size="sm"
              className="mt-5"
              onClick={() => goTo(page.action!.href)}
            >
              {page.action.label}
              <ArrowRight className="size-3.5" aria-hidden />
            </Button>
          )}
        </motion.div>

        <div className="flex flex-col gap-4 border-t border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <Stepper step={step} onSelect={setStep} />

          <div className="flex items-center justify-between gap-2 sm:justify-end">
            {!isLast && (
              <Button variant="ghost" size="sm" onClick={close}>
                Skip
              </Button>
            )}
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep((s) => s - 1)}
                >
                  <ArrowLeft className="size-3.5" aria-hidden />
                  Back
                </Button>
              )}
              {isLast ? (
                <Button size="sm" onClick={close}>
                  <Rocket className="size-3.5" aria-hidden />
                  Get started
                </Button>
              ) : (
                <Button size="sm" onClick={() => setStep((s) => s + 1)}>
                  Next
                  <ArrowRight className="size-3.5" aria-hidden />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The dots. Clickable, because a reader who skimmed past a page should be able
 * to go straight back to it without pressing Back three times. The current one
 * stretches into a bar, which reads as "you are here" without a number.
 */
function Stepper({
  step,
  onSelect,
}: {
  step: number;
  onSelect: (index: number) => void;
}) {
  return (
    <div
      className="flex items-center justify-center gap-1.5 sm:justify-start"
      role="group"
      aria-label={`Step ${step + 1} of ${TOUR_PAGES.length}`}
    >
      {TOUR_PAGES.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={`Go to step ${i + 1}: ${p.title}`}
          aria-current={i === step ? 'step' : undefined}
          className={cn(
            // `rounded-full` is the functional dot, which stays round when a
            // user picks square corners — see cornerTokens.test.ts.
            'h-2 rounded-full transition-all duration-300 motion-reduce:transition-none',
            i === step
              ? 'w-6 bg-primary'
              : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60',
          )}
        />
      ))}
    </div>
  );
}

/**
 * The picture at the top of each page: the page's icon on a soft gradient, with
 * two smaller ones floating beside it.
 *
 * Drawn from theme tokens only — primary, info, success and the chart hues — at
 * low alpha over `bg-card`, so dark mode gets a dark picture rather than a
 * light panel glued to a dark dialog. The float is decoration, so it stops
 * entirely for anyone who has asked their system for less motion.
 */
function TourIllustration({
  art,
  step,
  still,
}: {
  art: TourArt;
  step: number;
  still: boolean;
}) {
  const Main = art.main;
  const Left = art.left;
  const Right = art.right;

  return (
    <div
      aria-hidden
      className="relative flex h-36 items-center justify-center overflow-hidden border-b border-border bg-card bg-gradient-to-br from-primary/15 via-info/10 to-chart-2/15 sm:h-44"
    >
      {/* Two blurred washes for depth. Pure decoration. */}
      <div className="absolute -top-10 -left-10 size-40 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute -right-8 -bottom-12 size-40 rounded-full bg-chart-2/20 blur-3xl" />

      <Floating
        key={`left-${step}`}
        still={still}
        rotate={-8}
        rise={5}
        delay={0.2}
        className="absolute top-1/2 left-[18%] -mt-10 flex size-11 items-center justify-center rounded-xl border border-border bg-card text-info shadow-sm sm:left-[24%]"
      >
        <Left className="size-5" />
      </Floating>

      <Floating
        key={`main-${step}`}
        still={still}
        rotate={0}
        rise={6}
        delay={0}
        className="relative flex size-20 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30"
      >
        <Main className="size-9" />
      </Floating>

      <Floating
        key={`right-${step}`}
        still={still}
        rotate={8}
        rise={4}
        delay={0.5}
        className="absolute top-1/2 right-[18%] mt-2 flex size-11 items-center justify-center rounded-xl border border-border bg-card text-success shadow-sm sm:right-[24%]"
      >
        <Right className="size-5" />
      </Floating>
    </div>
  );
}

/**
 * One tile of the illustration: it pops in when its page opens, then bobs.
 *
 * The two movements run on separate clocks — the pop once, the bob forever —
 * which is why `y` has its own transition. Keyed by step in the caller, so a
 * new page gets a new pop rather than the old tiles swapping icons in place.
 */
function Floating({
  still,
  rotate,
  rise,
  delay,
  className,
  children,
}: {
  still: boolean;
  rotate: number;
  rise: number;
  delay: number;
  className: string;
  children: ReactNode;
}) {
  if (still) {
    return (
      <div className={className} style={{ transform: `rotate(${rotate}deg)` }}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.6, rotate: rotate * 1.5 }}
      animate={{ opacity: 1, scale: 1, rotate, y: [0, -rise, 0] }}
      transition={{
        default: { type: 'spring', stiffness: 260, damping: 18, delay },
        y: {
          duration: 3.2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: delay + 0.4,
        },
      }}
    >
      {children}
    </motion.div>
  );
}
